import { useEffect, useMemo, useState } from 'react';
import Router, { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import Image from 'next/image';

import getHeadingSkin from 'helpers/getHeadingSkin';
import getHeadingTag from 'helpers/getHeadingTag';
import { formatDateForQuery, formatToPageHeadline } from 'helpers/date';
import { cleanRouterQuery, mapObjectToQueryString } from 'helpers/url';
import { useLinkResolver } from 'prismic/helpers/useLinkResolver';

import {
  TagesplanItem,
  ApiFetchTagesplanResponse,
} from 'pages/api/tagesplanApi.types';


import { getPdfData } from 'components/molecules/TagesPlanPdfDownload/TagesPlanPdfDownload.helper';
import ComponentsRenderer from 'components/templates/partials/ComponentsRenderer';
import { TagesPlanPdfDownload } from 'components/molecules/TagesPlanPdfDownload';
import { EventBoxBig } from 'components/molecules/EventBoxBig';
import { Separator } from 'components/organisms/Separator';
import Container from 'components/layout/Grid/Container';
import { Heading } from 'components/atoms/Heading';
import { Loading } from 'components/atoms/Loading';
import { Breadcrumb } from 'components/molecules/Breadcrumb';
import { CtaDropdown } from 'components/molecules/CtaDropdown';

import {
  HeadingStyled,
  ColumnStyled,
  DatePickerStyled,
  DateStyled,
  RowDateSelectStyled,
  RowDownloadStyled,
  RowLoadingStyled,
  BgMotivTagesplanStyled,
  SortGroup,
  FilterControlsRowStyled,
  CategoryFilterRowStyled,
  CategoryButtonStyled,
} from './OverviewTagesPlan.styles';

interface TagesPlanProps {
  events: ApiFetchTagesplanResponse | null;
  data: any;
}

const normalizeCategoryKey = (category: unknown): string | null => {
  if (typeof category !== 'string') return null;

  const cleaned = category.trim();
  if (!cleaned) return null;

  const normalized = cleaned
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[\s_-]/g, '');

  if (['handwerk', 'handwerksdemonstration'].includes(normalized)) {
    return 'Handwerk';
  }

  if (
    [
      'mitmachaktivitaet',
      'mitmachaktivitat',
      'mitmachaktivitaeten',
      'mitmachaktivitaten',
    ].includes(normalized)
  ) {
    return 'Mitmachaktivitaet';
  }

  if (
    [
      'veranstaltung',
      'veranstaltungen',
      'event',
      'events',
      'manifestation',
      'manifestations',
      'manifestazione',
      'manifestazioni',
    ].includes(normalized)
  ) {
    return 'Veranstaltung';
  }

  return cleaned;
};

export const OverviewTagesPlan: React.FC<TagesPlanProps> = ({
  events,
  data,
}) => {
  const router = useRouter();
  const { t, lang } = useTranslation('common');
  const { locale } = useLinkResolver();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  /* ================= DEBUG ================= */
  useEffect(() => {
    console.log('=== API RESPONSE ===');
    console.log('events:', events);
    console.log('schedule:', events?.schedule);
  }, [events]);

  /* ================= DATE ================= */
  const selectedDateFromQuery = (): string => {
    const date = cleanRouterQuery(router.query).date;
    const value = Array.isArray(date) ? date[0] : date;

    if (typeof value === 'string' && value) {
      return value.slice(0, 10);
    }

    return formatDateForQuery(new Date());
  };

  const currentDate = (): Date => {
    return new Date(`${selectedDateFromQuery()}T00:00:00`);
  };

  /* ================= ROUTE LOADING ================= */
  useEffect(() => {
    const start = () => setIsLoading(true);
    const end = () => setIsLoading(false);

    Router.events.on('routeChangeStart', start);
    Router.events.on('routeChangeComplete', end);
    Router.events.on('routeChangeError', end);

    return () => {
      Router.events.off('routeChangeStart', start);
      Router.events.off('routeChangeComplete', end);
      Router.events.off('routeChangeError', end);
    };
  }, []);

  const isSortSelected = (): string => {
    const queryObj = cleanRouterQuery(router.query);
    const querySort = queryObj.sort;
    const singleQuerySort = Array.isArray(querySort) ? querySort[0] : querySort;
    return singleQuerySort || 'time-asc';
  };

  const sortOptions = [
    {
      label: t('sortTimeAsc'),
      value: 'time-asc',
    },
    {
      label: t('sortTimeDes'),
      value: 'time-desc',
    },
    {
      label: t('sortLocationAsc'),
      value: 'location-asc',
    },
    {
      label: t('sortLocationDes'),
      value: 'location-desc',
    },
  ];

  const onClickSort = (value: string): void => {
    const queryObj = cleanRouterQuery(router.query);
    if (value) {
      queryObj.sort = value;
    }

    router.push(
      `/${lang}${router.asPath.split('?')[0]}${mapObjectToQueryString(
        queryObj
      )}&lang=${lang}`
    );
  };

const flatItems = useMemo<TagesplanItem[]>(() => {
  const schedule = Array.isArray(events?.schedule) ? events!.schedule : [];

  const selectedDate = selectedDateFromQuery();
  if (!selectedDate) return [];

  const sortValue = isSortSelected();

  const parseTimeslotStart = (timeslot?: string | null): number => {
    if (!timeslot) return Number.MAX_SAFE_INTEGER;
    const match = timeslot.match(/(\d{1,2})[:.](\d{2})/);
    if (!match) return Number.MAX_SAFE_INTEGER;
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    return hours * 60 + minutes;
  };

  
  const mapped: TagesplanItem[] = schedule
    .filter((entry) => entry?.date?.slice(0, 10) === selectedDate)
    .map((entry, index) => {
      const safeDate = entry.date ?? selectedDate;

      return {
        _type: 'tagesplan' as TagesplanItem['_type'],
        _linkType: undefined,
        _meta: {
          uid: `${safeDate}-${index}`,
        } as any,

        display_name: entry.title ?? t('eventsPageTitle'),
        title: entry.title ?? t('eventsPageTitle'),
        category: normalizeCategoryKey(entry.category),

        image: entry.image ?? entry.thumbnail ?? null,
        description: entry.description ?? [],

        termin: {
          date: safeDate,
          timeslot: entry.time_slot ?? null,
          location: entry.location
            ? {
                name: entry.location.name ?? null,
                house_nr: entry.location.house_nr ?? null,
              }
            : null,
        },

        _dateToSort: new Date(safeDate),
      };
    });

  const compareLocation = (a: TagesplanItem, b: TagesplanItem) => {
    const houseA =
      typeof a.termin?.location?.house_nr === 'number'
        ? a.termin?.location?.house_nr
        : Number.MAX_SAFE_INTEGER;
    const houseB =
      typeof b.termin?.location?.house_nr === 'number'
        ? b.termin?.location?.house_nr
        : Number.MAX_SAFE_INTEGER;
    if (houseA !== houseB) return houseA - houseB;

    const nameA = (a.termin?.location?.name ?? '').toString().toLowerCase();
    const nameB = (b.termin?.location?.name ?? '').toString().toLowerCase();
    if (nameA !== nameB) return nameA.localeCompare(nameB);

    const titleA = (a.display_name ?? '').toString().toLowerCase();
    const titleB = (b.display_name ?? '').toString().toLowerCase();
    return titleA.localeCompare(titleB);
  };

  const compareTime = (a: TagesplanItem, b: TagesplanItem) => {
    const timeA = parseTimeslotStart(a.termin?.timeslot);
    const timeB = parseTimeslotStart(b.termin?.timeslot);
    if (timeA !== timeB) return timeA - timeB;
    return compareLocation(a, b);
  };

  const sorted = mapped.sort((a, b) => {
    if (sortValue === 'time-desc') return -compareTime(a, b);
    if (sortValue === 'location-asc') return compareLocation(a, b);
    if (sortValue === 'location-desc') return -compareLocation(a, b);
    return compareTime(a, b);
  });

  return sorted;
}, [events, router.query.date, router.query.sort, t]);

  /* ================= CATEGORIES ================= */
  useEffect(() => {
    setSelectedCategory(null);
  }, [router.query.date]);

  const availableCategories = useMemo(() => {
    const preferredOrder = ['Veranstaltung', 'Mitmachaktivitaet', 'Handwerk'];
    const cats = flatItems
      .map((item) => item.category)
      .filter((cat): cat is string => !!cat)
      .filter((cat, idx, arr) => arr.indexOf(cat) === idx);
    cats.sort((a, b) => {
      const idxA = preferredOrder.indexOf(a);
      const idxB = preferredOrder.indexOf(b);
      const orderA = idxA >= 0 ? idxA : preferredOrder.length;
      const orderB = idxB >= 0 ? idxB : preferredOrder.length;
      return orderA - orderB;
    });
    return cats;
  }, [flatItems]);

  const filteredItems = useMemo(() => {
    if (!selectedCategory) return flatItems;
    return flatItems.filter((item) => item.category === selectedCategory);
  }, [flatItems, selectedCategory]);

  /* ================= PDF ================= */
  const pdfContent = useMemo(() => {
    if (!isMounted || !filteredItems.length) return null;

    try {
      return getPdfData(
        {
          items: filteredItems,
          date: selectedDateFromQuery(),
        },
        t
      );
    } catch (err) {
      console.error('[OverviewTagesPlan] getPdfData error', err);
      return null;
    }
  }, [isMounted, filteredItems, router.query.date, t]);

  /* ================= FILTERS ================= */
  const onClickDatePicker = (date: Date): void => {
    const queryObj = cleanRouterQuery(router.query);
    queryObj.date = formatDateForQuery(date);

    router.push(
      `/${lang}${router.asPath.split('?')[0]}${mapObjectToQueryString(
        queryObj
      )}&lang=${lang}`
    );
  };

  const sortSelectionComponent = (
    <SortGroup>
      <span>{t('sortBy')}</span>
      <CtaDropdown
        key={`${lang}-${isSortSelected()}`}
        options={sortOptions}
        defaultValue={isSortSelected()}
        onSelectCallback={(value) => {
          onClickSort(value);
        }}
      />
    </SortGroup>
  );

  /* ================= RENDER ================= */
  return (
    <>
      <Container>
        <ColumnStyled span={12}>
          <BgMotivTagesplanStyled>
            <Image
              alt="Tagesplan motiv"
              src="/motivs/tagesplan-motiv1.svg"
              width={1555}
              height={1962}
            />
          </BgMotivTagesplanStyled>

          <Breadcrumb />

          <HeadingStyled
            skin={getHeadingSkin('heading1')}
            elementType={getHeadingTag('heading1')}
          >
            {t('eventsPageTitle')}
          </HeadingStyled>

          <DateStyled>
            {formatToPageHeadline(selectedDateFromQuery(), locale)}
          </DateStyled>

          <RowDateSelectStyled>
            {t('selectDate')}{' '}
            <DatePickerStyled
              currentDate={currentDate()}
              onChange={onClickDatePicker}
              displayDateFormat="dd.MM.yyyy"
            />
          </RowDateSelectStyled>

          <RowDownloadStyled>
            <TagesPlanPdfDownload
              slices={data?.data?.body ?? []}
              content={pdfContent}
            />
          </RowDownloadStyled>

          <Heading
            skin={getHeadingSkin('heading2')}
            elementType={getHeadingTag('heading2')}
          >
            {t('ourProgrammToday')}
          </Heading>

          <FilterControlsRowStyled>
            {availableCategories.length > 0 && (
              <CategoryFilterRowStyled>
                <CategoryButtonStyled
                  isActive={selectedCategory === null}
                  onClick={() => setSelectedCategory(null)}
                >
                  {t('allCategories')}
                </CategoryButtonStyled>
                {availableCategories.map((cat) => (
                  <CategoryButtonStyled
                    key={cat}
                    isActive={selectedCategory === cat}
                    onClick={() => setSelectedCategory(cat)}
                  >
                    {t(cat)}
                  </CategoryButtonStyled>
                ))}
              </CategoryFilterRowStyled>
            )}

            {sortSelectionComponent}
          </FilterControlsRowStyled>

          {isLoading && (
            <RowLoadingStyled>
              <Loading />
            </RowLoadingStyled>
          )}

          {!isLoading && flatItems.length === 0 && (
            <div>{t('noResults')}</div>
          )}

          {filteredItems.map((eventItem, index) => (
            <EventBoxBig
              key={`event-box-${index}`}
              data={eventItem}
            />
          ))}
        </ColumnStyled>
      </Container>

      <Separator primary={{ horizontal_line: true }} />
      <ComponentsRenderer slices={data?.data?.body ?? []} />
    </>
  );
};
